import { cn } from "@/lib/utils";
import {
  imageBusyness,
  layoutMarkers,
  isShortBoxSideTab,
  type PlacedMarker,
  type TabCorner,
} from "@webapp/utils/screenshot-markers";
import { useEffect, useMemo, useRef, useState } from "react";

// Percent of the image's width/height, so boxes stay put at any rendered size.
export interface ScreenshotBox {
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
}

const COLOUR = "#F5B83D";

// The outline's corner under an attached tab is squared off so the two read as one shape.
const SQUARE_CORNER: Record<TabCorner, string> = {
  "top-right": "rounded-tr-none",
  "top-left": "rounded-tl-none",
  "bottom-right": "rounded-br-none",
  "bottom-left": "rounded-bl-none",
  right: "rounded-r-none",
  left: "rounded-l-none",
};

function squareCornerFor(marker: PlacedMarker, corner: TabCorner) {
  const isSide = corner === "right" || corner === "left";
  if (isSide && !isShortBoxSideTab(marker.placement, marker.box, marker.tabHeight)) return "";
  return SQUARE_CORNER[corner];
}

const TAB_ROUNDING: Record<TabCorner, string> = {
  "top-right": "rounded-t-[4px]",
  "top-left": "rounded-t-[4px]",
  "bottom-right": "rounded-b-[4px]",
  "bottom-left": "rounded-b-[4px]",
  right: "rounded-r-[4px]",
  left: "rounded-l-[4px]",
};

const TAB_TEXT = "flex items-center justify-center leading-none font-bold text-black";

function px(box: { x: number; y: number; w: number; h: number }) {
  return { left: box.x, top: box.y, width: box.w, height: box.h };
}

const BORDER_WIDTH = 2;

// An attached tab reaches into the box's border so the two read as one shape,
// with no gap or dark outline line between them.
function tabRect(placement: PlacedMarker["placement"]) {
  const { tab } = placement;
  if (placement.kind !== "attached") return tab;
  if (placement.corner === "right")
    return { ...tab, x: tab.x - BORDER_WIDTH, w: tab.w + BORDER_WIDTH };
  if (placement.corner === "left") return { ...tab, w: tab.w + BORDER_WIDTH };
  if (placement.corner.startsWith("top")) return { ...tab, h: tab.h + BORDER_WIDTH };
  return { ...tab, y: tab.y - BORDER_WIDTH, h: tab.h + BORDER_WIDTH };
}

function Marker({ marker, number }: { marker: PlacedMarker; number: number }) {
  const { box, placement } = marker;
  const corner = placement.kind === "attached" ? placement.corner : null;
  return (
    <>
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute rounded-[5px] border-2 shadow-[0_0_0_1px_rgba(0,0,0,0.45)]",
          corner && squareCornerFor(marker, corner),
        )}
        style={{ ...px(box), borderColor: COLOUR }}
      />
      {placement.kind === "detached" && (
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 size-full overflow-visible"
        >
          <line
            x1={placement.lineFrom.x}
            y1={placement.lineFrom.y}
            x2={placement.tab.x + placement.tab.w / 2}
            y2={placement.tab.y + placement.tab.h / 2}
            stroke={COLOUR}
            strokeWidth={1.5}
          />
        </svg>
      )}
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute",
          TAB_TEXT,
          corner ? TAB_ROUNDING[corner] : "rounded-[4px]",
        )}
        style={{
          ...px(tabRect(placement)),
          backgroundColor: COLOUR,
          fontSize: Math.round(marker.tabHeight * 0.67),
        }}
        data-marker-coverage={marker.coverage.toFixed(2)}
      >
        {number}
      </span>
    </>
  );
}

export function AnnotatedScreenshot({
  src,
  alt,
  boxes,
}: {
  src: string;
  alt: string;
  boxes: ScreenshotBox[];
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Markers are laid out in rendered pixels, so they wait for the image's real size.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    // A server-rendered image can finish loading before hydration, so onLoad never fires.
    if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) setLoaded(true);
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (height > 0) setSize({ width, height });
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  // Sampled at rendered size so tabs can steer clear of text and icons in the shot.
  const busyness = useMemo(() => {
    const image = imageRef.current;
    if (!size || !loaded || !image) return undefined;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(size.width);
    canvas.height = Math.round(size.height);
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return undefined;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return imageBusyness(context.getImageData(0, 0, canvas.width, canvas.height));
  }, [size, loaded]);

  const markers = size && loaded ? layoutMarkers(boxes, size.width, size.height, busyness) : [];

  return (
    <figure className="not-prose my-8" data-testid="annotated-screenshot">
      <div className="overflow-hidden rounded-xl border border-white/10 bg-black">
        <div ref={frameRef} className="relative">
          <img
            ref={imageRef}
            src={src}
            alt={alt}
            loading="lazy"
            className="block w-full"
            onLoad={() => setLoaded(true)}
          />
          {markers.map((marker, index) => (
            <Marker
              key={`${boxes[index].x}-${boxes[index].y}`}
              marker={marker}
              number={index + 1}
            />
          ))}
        </div>
      </div>
      {boxes.length > 0 && (
        <ol className="mt-3 space-y-1 text-sm text-muted-foreground">
          {boxes.map((box, index) => (
            <li key={`${box.x}-${box.y}`} className="flex items-center gap-2">
              <span
                className={cn(
                  TAB_TEXT,
                  "h-[15px] min-w-[15px] shrink-0 rounded-[4px] px-[3px] text-[10px]",
                )}
                style={{ backgroundColor: COLOUR }}
              >
                {index + 1}
              </span>
              <span>{box.label}</span>
            </li>
          ))}
        </ol>
      )}
    </figure>
  );
}
